// Config-driven content for the public, indexable "Free Business Tools" pages.
// Each tool reuses an existing Fairview workflow concept via the public backend endpoint
// (/api/public/tools/:slug) and returns a limited, useful free preview + soft signup CTA.

export const TOOLS = {
  "business-name-generator": {
    slug: "business-name-generator",
    eyebrow: "Free Business Tool",
    h1: "Free AI Business Name Generator",
    metaTitle: "Free AI Business Name Generator",
    metaDescription:
      "Free AI business name generator: describe what you do and get creative, on-brand company name ideas in seconds. Works as a company name generator too — no signup required to start.",
    keyword: "business name generator",
    intro:
      "Stuck on what to call your company? Describe what you do and our free AI business name generator instantly gives you a shortlist of creative, professional company name ideas to spark your brand — no signup required to get started.",
    cardTitle: "Generate business names",
    submitLabel: "Generate names",
    resultTitle: "Your business name ideas",
    ctaHeadline: "Create a free account to save and continue your results",
    ctaSub: "Save your favorites, check availability, and build your brand inside Fairview Systems.",
    fields: [
      { key: "description", type: "textarea", label: "What does your business do?", required: true,
        placeholder: "e.g. A subscription box of locally roasted coffee for remote workers." },
      { key: "customer", type: "text", label: "Who is your target customer? (optional)",
        placeholder: "e.g. Remote professionals" },
      { key: "style", type: "select", label: "Preferred name style (optional)",
        options: ["Professional", "Modern", "Premium", "Friendly", "Bold", "Creative"] },
    ],
    software: { name: "Fairview AI Business Name Generator", category: "BusinessApplication" },
    howItWorks: [
      "Describe what your business does and (optionally) your audience and style.",
      "Our AI generates a shortlist of brandable name ideas across several styles.",
      "Pick your favorites, then create a free account to save them and check availability.",
    ],
    whatItDoes:
      "The business name generator uses AI to turn a plain-English description of your company into memorable, on-brand name ideas — grouped by style so you can quickly find a direction that fits.",
    whoFor:
      "Founders and side-hustlers who are naming a new company, product, or rebrand and want fast inspiration before locking in a name.",
    resultsMean:
      "The names are creative suggestions only. Trademark, domain, and state-registration availability are NOT verified — always confirm with the USPTO, your Secretary of State, and a domain registrar before committing.",
    nextSteps: [
      "Shortlist 2–3 favorite names.",
      "Check domain and trademark availability.",
      "Create a free account to save your shortlist and start forming your business.",
    ],
    faqs: [
      { q: "Is the business name generator really free?", a: "Yes. You can generate business name ideas for free without an account. Create a free account to save your results and continue building your brand." },
      { q: "Does it check if a name is available?", a: "No. The generator produces creative name ideas only. You should verify trademark, domain, and state-registration availability with official sources before using a name." },
      { q: "How many names will I get?", a: "You'll get a curated shortlist of around 10 name ideas grouped by style. Create a free account to generate and save more." },
      { q: "Is this a company name generator too?", a: "Yes — it works for any company or brand. Whether you call it a business name generator or a company name generator, you describe what you do and get on-brand name ideas back." },
      { q: "Does it use AI?", a: "Yes. This is an AI business name generator — it reads your plain-English description and generates brandable, relevant name ideas rather than pulling from a fixed word list." },
    ],
  },

  "business-idea-validator": {
    slug: "business-idea-validator",
    eyebrow: "Free Business Tool",
    h1: "Free Business Idea Validator",
    metaTitle: "Free Business Idea Validator (AI)",
    metaDescription:
      "Free business idea validator: test your business idea with an instant AI assessment of its strengths and risks. A quick idea tester — no signup required to start.",
    keyword: "business idea validator",
    intro:
      "Before you invest time and money, pressure-test your concept. Our free business idea validator is an instant idea tester that gives you a quick, honest read on your idea's strengths and the risks worth testing first.",
    cardTitle: "Validate your idea",
    submitLabel: "Validate my idea",
    resultTitle: "Your initial assessment",
    ctaHeadline: "Create a free account to unlock your full validation report and save your results",
    ctaSub: "Get market analysis, a validation checklist, and a step-by-step action plan inside Fairview Systems.",
    fields: [
      { key: "idea", type: "textarea", label: "What business idea are you considering?", required: true,
        placeholder: "e.g. A subscription box of locally roasted coffee for remote workers." },
      { key: "problem", type: "textarea", label: "What problem does it solve? (optional)",
        placeholder: "e.g. Remote workers struggle to find fresh, high-quality coffee." },
      { key: "customer", type: "text", label: "Who experiences this problem? (optional)",
        placeholder: "e.g. Remote knowledge workers, 25–45" },
    ],
    software: { name: "Fairview Business Idea Validator", category: "BusinessApplication" },
    howItWorks: [
      "Describe your idea and the problem it solves.",
      "Our AI returns an initial signal with your top strengths and risks to test.",
      "Create a free account to unlock the full validation report and action plan.",
    ],
    whatItDoes:
      "The business idea validator gives you a fast, objective initial assessment of your concept — a signal on how strong it looks, plus the top strengths to lean into and the biggest risks to test cheaply.",
    whoFor:
      "Aspiring founders deciding whether an idea is worth pursuing, and existing owners weighing a new product or market.",
    resultsMean:
      "The initial signal (Strong / Moderate / Needs work) is a directional read, not a guarantee of success. It's meant to focus your next validation experiments — the full report goes deeper into market fit and a step-by-step plan.",
    nextSteps: [
      "Note the risks flagged and design cheap experiments to test them.",
      "Talk to 5–10 potential customers to confirm the problem.",
      "Create a free account to unlock your full validation report and save your results.",
    ],
    faqs: [
      { q: "How does business idea validation work?", a: "You describe your idea and the problem it solves. The tool returns an initial signal with your top strengths and the key risks to test before investing." },
      { q: "Is this a full validation report?", a: "The free tool gives a limited initial assessment. Create a free account to unlock the full validation report, market analysis, and action plan." },
      { q: "Is my idea kept private?", a: "The free tool does not save your inputs to an account. Your assessment is generated on the fly. Create a free account if you'd like to save your results." },
      { q: "Is this a business idea tester?", a: "Yes — you can use it as a quick idea tester. It gives an instant signal on whether your idea looks strong and which risks to test before you invest, then the full report goes deeper." },
    ],
  },

  "business-plan-generator": {
    slug: "business-plan-generator",
    eyebrow: "Free Business Tool",
    h1: "Free AI Business Plan Generator",
    metaTitle: "Free AI Business Plan Generator",
    metaDescription:
      "Build your business plan online, free. Our AI business plan generator drafts your executive summary and a tailored plan outline in seconds — no signup to begin.",
    keyword: "AI business plan generator",
    intro:
      "Turn your idea into a real plan. Our free AI business plan generator drafts your executive summary and a tailored outline of every section your plan needs — so you can start writing today.",
    cardTitle: "Start your business plan",
    submitLabel: "Generate my starter plan",
    resultTitle: "Your business plan starter",
    ctaHeadline: "Create a free account to continue building your complete business plan",
    ctaSub: "Build every section, edit with AI, and export to PDF or Word inside Fairview Systems.",
    fields: [
      { key: "summary", type: "textarea", label: "In a sentence or two, what is your business?", required: true,
        placeholder: "e.g. We deliver fresh local coffee to remote workers on a weekly subscription." },
      { key: "solution", type: "textarea", label: "What is your product or service? (optional)",
        placeholder: "e.g. A curated weekly delivery of locally roasted beans." },
      { key: "market", type: "text", label: "Who is your target customer / market? (optional)",
        placeholder: "e.g. Remote professionals who value quality and convenience" },
    ],
    software: { name: "Fairview AI Business Plan Generator", category: "BusinessApplication" },
    howItWorks: [
      "Describe your business in a sentence or two.",
      "Our AI drafts an executive summary and a tailored plan outline.",
      "Create a free account to build out every section and export your plan.",
    ],
    whatItDoes:
      "The AI business plan generator creates a draft executive summary and a customized outline of the standard business plan sections — a running start you can build on section by section.",
    whoFor:
      "Founders who need a professional business plan for funding, formation, or clarity, and want to skip the blank page.",
    resultsMean:
      "The starter is a draft to build on, not a finished, investor-ready plan. It shows your executive summary and the sections to complete. Any figures are assumptions to replace with your own numbers.",
    nextSteps: [
      "Review the outline and gather the details for each section.",
      "Replace placeholder assumptions with your real numbers.",
      "Create a free account to complete, edit with AI, and export your full plan.",
    ],
    faqs: [
      { q: "Is the AI business plan generator free?", a: "Yes. You can generate a starter business plan (draft summary + outline) for free. Create a free account to build the complete plan and export it." },
      { q: "What do I get for free?", a: "A draft executive summary and a tailored outline of the sections your plan needs. The full section-by-section builder is available after you create a free account." },
      { q: "Can I export my business plan?", a: "Yes — inside Fairview Systems you can build every section and export your plan to PDF or Word after creating a free account." },
    ],
  },

  "business-structure-recommender": {
    slug: "business-structure-recommender",
    eyebrow: "Free Business Tool",
    h1: "LLC vs. S-Corp: Free Business Structure Recommender",
    metaTitle: "LLC vs. S-Corp: Free Business Structure Recommender",
    metaDescription:
      "LLC vs S-corp? Get a free educational recommendation on the right business structure for your situation. General information, not legal or tax advice.",
    keyword: "LLC vs S-corp",
    intro:
      "Not sure whether an LLC, S-Corp election, or another structure fits your business? Answer a few quick questions for a free, educational starting point on which options are worth considering.",
    cardTitle: "Get a structure recommendation",
    submitLabel: "Recommend a structure",
    resultTitle: "Your educational recommendation",
    ctaHeadline: "Create a free account to save your recommendation and continue your business setup",
    ctaSub: "Compare every entity type in depth and start forming your business inside Fairview Systems.",
    disclaimer:
      "This tool provides general educational information only. It is not legal, tax, or financial advice. Always confirm your decision with a qualified attorney or accountant.",
    fields: [
      { key: "state", type: "text", label: "What state will the business operate in?", required: true,
        placeholder: "e.g. Delaware, California, Texas" },
      { key: "owners", type: "select", label: "Will there be one owner or multiple owners?", required: true,
        options: ["One owner", "Multiple owners"] },
      { key: "liability", type: "select", label: "How important is liability protection? (optional)",
        options: ["Very important", "Somewhat important", "Not a priority"] },
      { key: "investors", type: "select", label: "Do you expect outside investors? (optional)",
        options: ["Yes", "No", "Maybe later"] },
    ],
    software: { name: "Fairview Business Structure Recommender", category: "BusinessApplication" },
    howItWorks: [
      "Answer a few quick questions about your business and goals.",
      "Get an educational read on which structures are worth considering.",
      "Create a free account to compare every option in depth and start forming your entity.",
    ],
    whatItDoes:
      "The business structure recommender compares common options — like a Single-Member LLC, Multi-Member LLC, and the S-Corp tax election — and points you toward the ones most worth considering for your situation.",
    whoFor:
      "New founders choosing how to legally structure their business who want a clear, jargon-free starting point before talking to a professional.",
    resultsMean:
      "The recommendation highlights options to consider and their trade-offs — it is educational information, not a legal or tax conclusion. Your final choice should be confirmed with a qualified attorney or accountant.",
    nextSteps: [
      "Review the trade-offs for the suggested structures.",
      "Confirm the right choice with a qualified attorney or accountant.",
      "Create a free account to save your recommendation and start forming your business.",
    ],
    faqs: [
      { q: "What's the difference between an LLC and an S-Corp?", a: "An LLC is a legal business structure, while an S-Corp is a tax election that an LLC or corporation can make. Many owners form an LLC and then elect S-Corp taxation. This tool gives an educational overview — confirm specifics with a tax professional." },
      { q: "Is this legal or tax advice?", a: "No. The recommender provides general educational information only. It is not legal, tax, or financial advice. Always confirm your decision with a qualified attorney or accountant." },
      { q: "Which structure is best for a single owner?", a: "It depends on your goals, liability needs, and tax situation. The tool will suggest options to consider (such as a Single-Member LLC, sometimes with an S-Corp election) as an educational starting point." },
    ],
  },
};

export const TOOL_LIST = Object.values(TOOLS).map((t) => ({ slug: t.slug, h1: t.h1, keyword: t.keyword }));

export const PROD_ORIGIN = "https://www.fairviewsystems.net";
